import { customAlphabet } from 'nanoid';
const nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz-', 10);
import { LicenseArea } from '../area-models/license-area.model';
import { CancelReport } from '../report-models/cancel-report.model';
import { DailyCatchReport } from '../report-models/daily-catch-report.model';
import { EndReport } from '../report-models/end-report.model';
import { LostRecoveredGearForm } from '../report-models/lost-recovered-found-gear.model';
import { MarineMammalInteractionReport } from '../report-models/marine-mammal-interaction-form.model';
import { OffloadReport } from '../report-models/offload-report.model';
import { PauseReport } from '../report-models/pause-report.model';
import { SpeciesAtRiskReport } from '../report-models/species-at-risk-report.model';
import { StartReport } from '../report-models/start-report.model';
import { Logbook } from '../user-models/logbook.model';
import { VesselData } from '../user-models/vessel-data.model';



export class Trip {

  /* General Trip Data */

  docID: string = new Date().toISOString();  // temporary way for storing unique Pouch document IDs

  tripID: string = '';

  fin: string = '';

  logbook: Logbook = new Logbook();

  vessel: VesselData = new VesselData();

  licenseArea: LicenseArea = new LicenseArea();

  /* Flags for programmatical use */

  offloadNeeded: boolean = false; // used to determine if the trip may require an offload report

  unsubmittedReports: Date[] = []; // keeps track of the number of reports that have been completed but not submitted

  // internalIds of reports the user has deleted. Kept as tombstones because merges (both the
  // save-time FOS merge and conflict resolution) key reports by internalId and take the union
  // across revisions -- without a record of the deletion they silently re-add the report
  deletedReportIds: string[] = [];

  /* Fishing Report Data */

  startFishingReport: StartReport = new StartReport();

  endFishingReport: EndReport = new EndReport();

  cancelFishingReport: CancelReport = new CancelReport();

  pauseFishingReports: PauseReport[] = []; // array of all pause reports submitted for the trip

  dailyCatchReports: DailyCatchReport[] = []; // array of all daily catch reports submitted for the trip

  offloadReports: OffloadReport[] = [];  // array of all offload reports submitted for the trip

  speciesAtRiskReports: SpeciesAtRiskReport[] = [];  // array of all spcies at risk reports submitted for the trip

  marineMammalInteractionReports: MarineMammalInteractionReport[] = [];

  lostRecoveredFoundGearReports: LostRecoveredGearForm[] = [];

  // the trip properties holding lists of reports, i.e. the ones a report can be deleted from
  public static readonly reportListNames: string[] = [
    'pauseFishingReports',
    'dailyCatchReports',
    'offloadReports',
    'speciesAtRiskReports',
    'marineMammalInteractionReports',
    'lostRecoveredFoundGearReports'
  ];


  constructor() {
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const datePrefix = `${year}-${month}-${day}`;

    // Generate the nanoid and combine with date prefix
    this.tripID = `${datePrefix}-${nanoid()}`.toUpperCase(); //needs to be only uppercase since FOS turns all letters capital in the response email

  }

  public toString(): string {
    return 'Logbook: ' + this.logbook + '; Vessel: ' + this.vessel + '; License Area: ' + this.licenseArea;
  }

  public isValid(): boolean {
    return this.logbook.isValid() && this.vessel.isValid() && this.licenseArea.isValid();
  }

  public getStartDate(): Date {
    return this.startFishingReport.startDate;
  }

  public getEndDate(): Date {
    if (this.cancelFishingReport.isComplete) {
      return this.getStartDate();
    } else {
      return this.endFishingReport.endDate;
    }
  }

  // convert object to Trip
  public static transformAnonymous(trip: any): Trip {

    // if trip does not exist, set a default one
    if (!trip) {
      return new Trip();
    }

    let userTrip: Trip = new Trip();
    userTrip.docID = trip.docID ?? new Date().toISOString();
    userTrip.tripID = trip.tripID ?? '123456';
    // FINs added through the "Add new vessel master" dialog used to be stored as numbers,
    // so coerce on read to keep comparisons against the saved string FIN options working
    userTrip.fin = trip.fin != null ? String(trip.fin) : '';
    userTrip.logbook = Logbook.transformAnonymous(trip.logbook);
    userTrip.vessel = VesselData.transformAnonymous(trip.vessel);
    userTrip.licenseArea = LicenseArea.transformAnonymous(trip.licenseArea);
    userTrip.offloadNeeded = trip.offloadNeeded ?? false;
    if (trip.unsubmittedReports) {
      userTrip.unsubmittedReports = trip.unsubmittedReports.slice(0);
    } else {
      userTrip.unsubmittedReports = [];
    }
    userTrip.deletedReportIds = Array.isArray(trip.deletedReportIds) ? trip.deletedReportIds.slice(0) : [];
    userTrip.startFishingReport = StartReport.transformAnonymous(trip.startFishingReport);
    userTrip.endFishingReport = EndReport.transformAnonymous(trip.endFishingReport);
    userTrip.cancelFishingReport = CancelReport.transformAnonymous(trip.cancelFishingReport);
    userTrip.pauseFishingReports = PauseReport.transformAnonymous(trip.pauseFishingReports);
    userTrip.dailyCatchReports = DailyCatchReport.transformAnonymous(trip.dailyCatchReports);
    userTrip.offloadReports = OffloadReport.transformAnonymous(trip.offloadReports);
    userTrip.speciesAtRiskReports = SpeciesAtRiskReport.transformAnonymous(trip.speciesAtRiskReports);
    userTrip.marineMammalInteractionReports = MarineMammalInteractionReport.transformAnonymous(trip.marineMammalInteractionReports);
    userTrip.lostRecoveredFoundGearReports = LostRecoveredGearForm.transformAnonymous(trip.lostRecoveredFoundGearReports);
    return userTrip;

  }

  // transforms an array of documents into an array of trips
  public static transformAnonArray(docs: any): Trip[] {

    if (!docs) { // check if the user's previous trips are all defined
      return []; // if they are not defined, return an empty array
    }

    let prevTripList: Trip[] = [];
    for (let doc of docs) {
      prevTripList.push(Trip.transformAnonymous(doc.trip));
    }
    return prevTripList;

  }

}
